import { useCallback, useMemo } from "react";

import { useFilterContext } from "../context/FilterContext";

export interface UseRangeFilterOptions<T> {
  queryKeyMin: string;
  queryKeyMax: string;
  parseValue: (raw: string) => T | undefined;
  serializeValue: (value: T) => string;
  formatLabel: (min: T | undefined, max: T | undefined) => string | undefined;
}

export interface UseRangeFilterResult<T> {
  min: T | undefined;
  max: T | undefined;
  setRange: (min: T | undefined, max: T | undefined) => void;
  clearRange: () => void;
  chipLabel: string | undefined;
}

export const useRangeFilter = <T>({
  queryKeyMin,
  queryKeyMax,
  parseValue,
  serializeValue,
  formatLabel,
}: UseRangeFilterOptions<T>): UseRangeFilterResult<T> => {
  const { filterState, updateFilters } = useFilterContext();

  const min = useMemo(() => {
    const raw = filterState[queryKeyMin] ?? null;
    return raw === null ? undefined : parseValue(raw);
  }, [filterState, queryKeyMin, parseValue]);

  const max = useMemo(() => {
    const raw = filterState[queryKeyMax] ?? null;
    return raw === null ? undefined : parseValue(raw);
  }, [filterState, queryKeyMax, parseValue]);

  const setRange = useCallback(
    (nextMin: T | undefined, nextMax: T | undefined) => {
      updateFilters({
        [queryKeyMin]:
          nextMin !== undefined ? serializeValue(nextMin) : undefined,
        [queryKeyMax]:
          nextMax !== undefined ? serializeValue(nextMax) : undefined,
      });
    },
    [queryKeyMin, queryKeyMax, serializeValue, updateFilters]
  );

  const clearRange = useCallback(() => {
    updateFilters({
      [queryKeyMin]: undefined,
      [queryKeyMax]: undefined,
    });
  }, [queryKeyMin, queryKeyMax, updateFilters]);

  const chipLabel = useMemo(
    () => formatLabel(min, max),
    [formatLabel, min, max]
  );

  return { min, max, setRange, clearRange, chipLabel };
};
